import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Spline from '@splinetool/react-spline';
import { Linkedin, Twitter, Mail } from 'lucide-react';
import './ComingSoonPage.css';

const ComingSoonPage = () => {
  const [splineError, setSplineError] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [splineKey, setSplineKey] = useState(0); // Force remount key
  const splineRef = useRef(null);
  const cleanupTimeoutRef = useRef(null);
  const memoryCheckIntervalRef = useRef(null);
  const reloadTimeoutRef = useRef(null);
  const frameCountRef = useRef(0);
  const lastFrameTimeRef = useRef(Date.now());
  const crashPreventionTimeoutRef = useRef(null);
  
  // Cleanup Spline on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (splineRef.current) {
        try {
          splineRef.current.dispose?.();
        } catch (error) {
          console.warn('Spline cleanup warning:', error);
        }
      }
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
      if (memoryCheckIntervalRef.current) {
        clearInterval(memoryCheckIntervalRef.current);
      }
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
      }
      if (crashPreventionTimeoutRef.current) {
        clearTimeout(crashPreventionTimeoutRef.current);
      }
    };
  }, []);

  // Aggressive memory management: Check every 10 seconds
  useEffect(() => {
    const checkMemory = () => {
      try {
        if (performance.memory) {
          const used = performance.memory.usedJSHeapSize;
          const limit = performance.memory.jsHeapSizeLimit;
          const percentage = (used / limit) * 100;
          
          // If memory is high (80%+), reload Spline to prevent crash
          if (percentage > 80 && splineRef.current) {
            console.warn('High memory usage detected, reloading Spline to prevent crash...');
            // Dispose and reload Spline
            try {
              splineRef.current.dispose?.();
            } catch (error) {
              console.warn('Spline disposal error:', error);
            }
            // Force remount by changing key
            setSplineKey(prev => prev + 1);
            setSplineError(false);
            setIsVisible(true);
          }
        }
      } catch (error) {
        // Memory API not available, continue normally
      }
    };

    memoryCheckIntervalRef.current = setInterval(checkMemory, 10000); // Check every 10 seconds
    
    return () => {
      if (memoryCheckIntervalRef.current) {
        clearInterval(memoryCheckIntervalRef.current);
      }
    };
  }, []);

  // Auto-reload Spline every 3 minutes to prevent memory buildup and crashes
  useEffect(() => {
    if (!splineError && isVisible) {
      reloadTimeoutRef.current = setTimeout(() => {
        console.log('Auto-reloading Spline to prevent memory buildup...');
        if (splineRef.current) {
          try {
            splineRef.current.dispose?.();
          } catch (error) {
            console.warn('Spline disposal on reload:', error);
          }
        }
        // Force remount
        setSplineKey(prev => prev + 1);
        setSplineError(false);
      }, 180000); // 3 minutes
    }

    return () => {
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
      }
    };
  }, [splineError, isVisible]);

  // Monitor frame rate to detect animation breaking
  useEffect(() => {
    if (!splineError && isVisible) {
      const checkFrameRate = () => {
        const now = Date.now();
        const timeDiff = now - lastFrameTimeRef.current;
        frameCountRef.current++;

        // Check every second
        if (frameCountRef.current >= 30) {
          const fps = (frameCountRef.current / timeDiff) * 1000;
          
          // If frame rate drops too low, Spline is breaking
          if (fps < 10 && splineRef.current) {
            console.warn('Low frame rate detected, reloading Spline...');
            try {
              splineRef.current.dispose?.();
            } catch (error) {
              console.warn('Spline disposal on low FPS:', error);
            }
            setSplineKey(prev => prev + 1);
            setSplineError(false);
            frameCountRef.current = 0;
          } else {
            frameCountRef.current = 0;
            lastFrameTimeRef.current = now;
          }
        }

        requestAnimationFrame(checkFrameRate);
      };

      const animationFrame = requestAnimationFrame(checkFrameRate);
      
      return () => {
        cancelAnimationFrame(animationFrame);
      };
    }
  }, [splineError, isVisible]);

  // WebGL compatibility check
  useEffect(() => {
    const checkWebGLSupport = () => {
      try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl2') || canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        
        if (!gl) {
          console.warn('WebGL not supported, using fallback');
          setSplineError(true);
          setIsVisible(false);
          return false;
        }
        
        // Check for WebGL2 specific features
        if (gl instanceof WebGL2RenderingContext) {
          // WebGL2 is available
          if (!gl.clearBufferfv) {
            console.warn('WebGL2 clearBufferfv not available, may cause issues');
          }
        }
        
        return true;
      } catch (error) {
        console.warn('WebGL check failed:', error);
        setSplineError(true);
        setIsVisible(false);
        return false;
      }
    };

    checkWebGLSupport();
  }, []);

  // Enhanced error boundary - catch and recover from Spline errors including WebGL errors
  useEffect(() => {
    const handleGlobalError = (event) => {
      const errorMessage = event.error?.message || '';
      const errorString = errorMessage.toString().toLowerCase();
      const errorStack = event.error?.stack || '';
      
      // Handle Spline-related errors including WebGL clearBufferfv errors
      if (errorString.includes('spline') || 
          errorString.includes('webgl') || 
          errorString.includes('canvas') || 
          errorString.includes('memory') ||
          errorString.includes('clearbufferfv') ||
          errorStack.includes('clearBufferfv') ||
          errorStack.includes('clearBuffer')) {
        console.error('Spline/WebGL error caught, switching to fallback:', event.error);
        
        // Immediately disable Spline and use fallback
        setSplineError(true);
        setIsVisible(false);
        
        // Dispose current instance
        if (splineRef.current) {
          try {
            splineRef.current.dispose?.();
          } catch (error) {
            console.warn('Spline disposal on error:', error);
          }
        }
        
        // Prevent error from propagating
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handleUnhandledRejection = (event) => {
      const reason = event.reason?.toString().toLowerCase() || '';
      const reasonStack = event.reason?.stack || '';
      
      if (reason.includes('spline') || 
          reason.includes('webgl') || 
          reason.includes('memory') ||
          reason.includes('clearbufferfv') ||
          reasonStack.includes('clearBufferfv')) {
        console.error('Spline/WebGL promise rejection, switching to fallback:', event.reason);
        
        // Immediately disable Spline
        setSplineError(true);
        setIsVisible(false);
        
        if (splineRef.current) {
          try {
            splineRef.current.dispose?.();
          } catch (error) {
            console.warn('Spline disposal on rejection:', error);
          }
        }
        
        // Prevent unhandled rejection
        event.preventDefault();
      }
    };

    // Use capture phase to catch errors early
    window.addEventListener('error', handleGlobalError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleGlobalError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Performance monitoring
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Pause when tab is not visible
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  const handleLoad = useCallback((spline) => {
    if (spline) {
      splineRef.current = spline;
      console.log('Spline loaded successfully');
      
      // Aggressive performance optimizations to prevent crashes
      try {
        // Limit pixel ratio to reduce memory usage
        if (spline.setPixelRatio) {
          const maxPixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
          spline.setPixelRatio(maxPixelRatio);
        }
        
        // Try to enable frame rate limiting if available
        if (spline.setFrameRate) {
          spline.setFrameRate(30); // Limit to 30fps instead of 60fps
        }
        
        // Disable anti-aliasing if available to reduce GPU load
        if (spline.setAntialiasing) {
          spline.setAntialiasing(false);
        }
      } catch (error) {
        console.warn('Spline optimization warning:', error);
      }
      
      // Periodic cleanup to prevent memory buildup - more frequent
      const cleanupInterval = setInterval(() => {
        if (splineRef.current) {
          // Force garbage collection hint
          if (window.gc) {
            window.gc();
          }
          // Clear any cached textures/geometry
          try {
            const canvas = document.querySelector('.spline-container canvas');
            if (canvas) {
              const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');
              if (gl) {
                // Clear WebGL resources
                gl.flush();
                gl.finish();
              }
            }
          } catch (error) {
            // Ignore cleanup errors
          }
        }
      }, 60000); // Every 1 minute - more frequent cleanup
      
      cleanupTimeoutRef.current = cleanupInterval;
    }
  }, []);
  
  const handleError = useCallback((error) => {
    console.error('Spline loading/rendering error - using fallback:', error);
    
    // Check if it's a WebGL error
    const errorMessage = error?.message || error?.toString() || '';
    if (errorMessage.includes('clearBufferfv') || 
        errorMessage.includes('WebGL') ||
        errorMessage.includes('clearBuffer')) {
      console.warn('WebGL compatibility issue detected, using fallback animation');
    }
    
    setSplineError(true);
    setIsVisible(false);
  }, []);

  return (
    <div className="coming-soon-container">
      <div className="grid-overlay"></div>
      
      <div className="content-wrapper">
        {/* Left Side - Content */}
        <motion.div 
          className="left-content"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          {/* Coming Soon Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="badge-container"
          >
            <div className="coming-soon-badge">COMING SOON</div>
          </motion.div>

          {/* Logo/Brand */}
          <motion.div 
            className="logo-section"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <h1 className="logo-text">AA</h1>
            <p className="company-name">ANANTLA AUSTRALIA</p>
          </motion.div>

          {/* Main Heading */}
          <motion.div
            className="main-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <h2 className="headline">
              We're building something exciting — a next-gen remittance experience for the world.
            </h2>
            
            <p className="subtext">
              Partnering and integrating with leading financial innovators to redefine cross-border payments.
            </p>
          </motion.div>

          {/* Social Media Icons */}
          <motion.div 
            className="social-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            <p className="social-label">CONNECT WITH US</p>
            <div className="social-icons">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn">
                <Linkedin size={24} />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Twitter">
                <Twitter size={24} />
              </a>
              <a href="mailto:contact@anantla.com" className="social-icon" aria-label="Email">
                <Mail size={24} />
              </a>
            </div>
          </motion.div>
        </motion.div>

        {/* Right Side - 3D Spline Element */}
        <motion.div 
          className="right-content"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, ease: 'easeOut', delay: 0.4 }}
        >
          <div className="spline-container">
            {!splineError && isVisible ? (
              <Spline 
                key={splineKey}
                scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode"
                onLoad={handleLoad}
                onError={handleError}
              />
            ) : (
              <div className="spline-fallback">
                <div className="spline-placeholder">
                  <div className="spherical-glow"></div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
      
      {/* Footer */}
      <motion.footer 
        className="footer"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.8 }}
      >
        <div className="footer-content">
          <div className="footer-logo">
            <img 
              src="/austrac-logo.png" 
              alt="AUSTRAC - Australian Transaction Reports and Analysis Centre" 
              className="austrac-logo"
            />
          </div>
          <p className="footer-text">
            Anantla Australia Pty Ltd is registered AUSTRAC Remittance License company. © All rights reserved
          </p>
        </div>
      </motion.footer>
    </div>
  );
};

export default ComingSoonPage;